import {
    AfterViewInit,
    Component,
    EventEmitter,
    Inject,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import { OverviewDataSource } from '../../../overview/overview-data-source';
import { MatPaginator } from '@angular/material/paginator';
import { catchError, filter, mergeMap } from 'rxjs/operators';
import { EMPTY } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { openQuoteCreationDialog } from '../quote-creation-dialog/quote-creation-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { OverlayDialogHelper } from '../../utils/overlay-dialog.helper';
import { ImageDialogComponent, ImageDialogData } from './image-dialog/image-dialog.component';
import { DOCUMENT } from '@angular/common';
import { PaginationService } from '../../services/paginator.service';
import { TemplateItem } from '../../../overview/overview.data.service';
import { openQuoteEditDialog } from '../quote-edit-dialog/quote-edit-dialog.component';
import { openQuoteDeleteDialog } from '../quote-delete-dialog/quote-delete-dialog.component';

export interface TemplateEvent {
    id: string;
}

interface UseTemplateEvent extends TemplateEvent {
    customerPurchaseNumber: string;
}

export interface EditTemplateEvent extends UseTemplateEvent {
    primarySchemes: string;
    templateDescription: string;
}

@Component({
    selector: 'app-templates-table',
    templateUrl: './templates-table.component.html',
    styleUrls: ['./templates-table.component.scss'],
})
export class TemplatesTableComponent implements OnInit, AfterViewInit, OnDestroy {
    private helper: OverlayDialogHelper<ImageDialogComponent, ImageDialogData>;

    @ViewChild(MatPaginator, { static: true })
    public paginator: MatPaginator;

    @Input()
    public dataSource: OverviewDataSource<TemplateItem, any>;

    @Input()
    public isAdmin: boolean;

    @Output()
    public use = new EventEmitter<UseTemplateEvent>();

    @Output()
    public edit = new EventEmitter<EditTemplateEvent>();

    @Output()
    public delete = new EventEmitter<TemplateEvent>();

    @Output()
    public pagingError = new EventEmitter<any>();
    @Output()
    public sortError = new EventEmitter<any>();

    constructor(
        @Inject(DOCUMENT) document: Document,
        private translationService: TranslateService,
        private dialog: MatDialog,
        private paginatorService: PaginationService
    ) {
        this.helper = new OverlayDialogHelper<ImageDialogComponent, ImageDialogData>(
            document,
            dialog,
            ImageDialogComponent,
            'image-dialog'
        );
    }

    ngOnInit(): void {
        this.dataSource.init(this.paginator);
        this.translatePaginator();
    }

    public onSelectTemplate({ id, description, customerPurchaseNumber }: TemplateItem) {
        openQuoteCreationDialog(this.dialog, {
            title: this.translationService.instant('OVERVIEW.DIALOG.USE_TEMPLATE.TEMPLATE_TITLE', {
                title: customerPurchaseNumber,
            }),
            confirm: this.translationService.instant('OVERVIEW.DIALOG.USE_TEMPLATE.CONFIRM'),
            cancel: this.translationService.instant('COMMON.DIALOG.CANCEL'),
            description: this.translationService.instant('OVERVIEW.DIALOG.USE_TEMPLATE.DESCRIPTION'),
            templateDescription: this.translationService.instant('OVERVIEW.DIALOG.USE_TEMPLATE.TEMPLATE_DESCRIPTION', {
                description,
            }),
        }).subscribe((title) => {
            if (title) {
                this.use.emit({ id, customerPurchaseNumber });
            }
        });
    }

    public onEditTemplate(template: TemplateItem) {
        openQuoteEditDialog(this.dialog, {
            templateDescription: template.description,
            customerPurchaseNumber: template.customerPurchaseNumber,
            primarySchemes: template.scheme?.id,
        })
            .pipe(filter(Boolean))
            .subscribe(({ customerPurchaseNumber, primarySchemes, templateDescription }) => {
                this.edit.emit({
                    id: template.id,
                    customerPurchaseNumber,
                    primarySchemes,
                    templateDescription,
                });
            });
    }

    public onDeleteTemplate({ id, customerPurchaseNumber }: TemplateItem) {
        openQuoteDeleteDialog(
            this.dialog,
            this.translationService.instant('OVERVIEW.DIALOG.DELETE_TEMPLATE.TITLE'),
            this.translationService.instant('OVERVIEW.DIALOG.DELETE_TEMPLATE.DESCRIPTION'),
            this.translationService.instant('OVERVIEW.DIALOG.DELETE_TEMPLATE.MESSAGE', { customerPurchaseNumber }),
            this.translationService.instant('COMMON.DIALOG.CONFIRM'),
            this.translationService.instant('COMMON.DIALOG.CANCEL')
        )
            .pipe(filter(Boolean))
            .subscribe(() => this.delete.emit({ id }));
    }

    public onImageHover(event: MouseEvent, obj) {
        this.helper.handleEvent(event, {
            id: obj.documentId,
            imageUrl: obj.schemePreview,
        });
    }

    private translatePaginator() {
        const intl = this.paginator._intl;
        intl.itemsPerPageLabel = this.translationService.instant('OVERVIEW.TABLE.PAGINATOR.LABEL.ITEMS_PER_PAGE');
        intl.getRangeLabel = (page: number, pageSize: number, length: number) => {
            const start = page * pageSize;
            const end = start < length ? Math.min(start + pageSize, length) : start + pageSize;
            return this.translationService.instant('OVERVIEW.TABLE.PAGINATOR.LABEL.RANGE', {
                page: start + 1,
                pageSize: end,
                length,
            });
        };
        intl.changes.next();
    }

    public ngAfterViewInit(): void {
        // listen to paging events
        this.paginator.page
            .pipe(
                mergeMap(() =>
                    this.dataSource.loadOverviewData({
                        parameters: {
                            sortBy: 'description',
                            sortOrder: 'asc',
                        },
                        pageSize: 50,
                    })
                ),
                catchError(() => {
                    this.pagingError.emit();
                    return EMPTY;
                })
            )
            .subscribe();
    }

    getTotalPages(): number {
        return this.paginatorService.getTotalPages(this.paginator);
    }

    goToFirstPage() {
        this.paginatorService.goToFirstPage(this.paginator);
    }

    goToPreviousPage() {
        this.paginatorService.goToPreviousPage(this.paginator);
    }

    goToNextPage() {
        this.paginatorService.goToNextPage(this.paginator);
    }

    goToLastPage() {
        this.paginatorService.goToLastPage(this.paginator);
    }

    public ngOnDestroy(): void {
        this.helper.destroy();
    }
}
